import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { acceptHMRUpdate } from 'pinia';
import { licenseService } from '~/services/license-service';
import { useAuthStore } from '~/stores/auth';
import { usePersonStore } from '~/stores/person';
import { useBillingStore } from '~/stores/person/billing';
import { LICENSE_BENEFITS, LicenseRenewalTerm, LicenseType, PurchaseType, SubscriptionType, type PurchaseLicenseFor, type StandardLicenseCostsResponse, type UpgradePricingResponse } from '~/types/licenses/licenses';
import type { License } from '~/types/person/person';


export const useSubscribeStore = defineStore('subscribe', () => {
    const purchasingFor = ref<number | null>(null);
    const purchaseType = ref<PurchaseType>(PurchaseType.PURCHASE);
    const license = ref<License | null>(null);

    const selectedTerm = ref<LicenseRenewalTerm>(LicenseRenewalTerm.YEARLY);
    const selectedPlanType = ref<LicenseType>(LicenseType.PRO);
    const selectedUserType = ref<SubscriptionType>(SubscriptionType.INDIVIDUAL);
    const licenseCount = ref(1);

    const paymentMethodId = ref('');
    const isUpdateCurrentLicense = ref(false);
    const isSimulationUpgrade = ref(false);
    const giftCertCode = ref('');
    const giftCertAmount = ref(0);
    const giftCertEmail = ref('');

    const giftCertAppliedCode = ref('');
    const giftCertAppliedAmount = ref(0);
    const giftCertName = ref('');
    
    // Promo code state
    const promoCode = ref<string | null>(null);
    const promoCodeData = ref<any>(null);
    const promoCodeApplied = ref(false);
    const urlPromoCode = ref<string | null>(null);
    
    // License costs from API
    const licenseCosts = ref<StandardLicenseCostsResponse | null>(null);
    const isLoadingLicenseCosts = ref(false);

    // Upgrade pricing from API
    const upgradePricing = ref<UpgradePricingResponse | null>(null);
    const isLoadingUpgradePricing = ref(false);

    // Get billing store for credit balance
    const billingStore = useBillingStore();
    const personStore = usePersonStore();

    // Fetch license costs from API
    async function fetchLicenseCosts() {
        isLoadingLicenseCosts.value = true;
        try {
            licenseCosts.value = await licenseService.getLicenseCosts();
        } catch (error: any) {
            console.error('Error fetching license costs:', error);
        } finally {
            isLoadingLicenseCosts.value = false;
        }
    }

    // Fetch upgrade pricing from API
    async function fetchUpgradePricing() {
        if (!license.value || purchaseType.value !== PurchaseType.UPGRADE) {
            upgradePricing.value = null;
            return;
        }

        const personStore = usePersonStore();
        if (!personStore.person?.id) {
            console.error('No person ID found');
            return;
        }

        // Clear old pricing data to force loading state
        upgradePricing.value = null;
        isLoadingUpgradePricing.value = true;
        
        try {
            upgradePricing.value = await licenseService.calculateUpgradePrice(
                personStore.person.id,
                license.value.id,
                {
                    new_license: selectedPlanType.value,
                    term: selectedTerm.value
                }
            );
        } catch (error: any) {
            console.error('Error fetching upgrade pricing:', error);
            upgradePricing.value = null;
        } finally {
            isLoadingUpgradePricing.value = false;
        }
    }

    function setGiftCertificateDetails(code: string, email: string, name: string, amount: number) {
        giftCertCode.value = code;
        giftCertEmail.value = email;
        giftCertAmount.value = amount;
        giftCertName.value = name;
    }

    function applyGiftCertificate(code: string, amount: number) {
        giftCertAppliedCode.value = code;
        giftCertAppliedAmount.value = amount;
    }

    function clearGiftCard() {
        giftCertAppliedCode.value = '';
        giftCertAppliedAmount.value = 0;
    }

    // Validate promo code
    interface PromoCodeValidationData {
        valid: boolean;
        code: string;
        discount_type: 'percentage' | 'fixed';
        discount_value: number;
        min_quantity: number | null;
        required_quantity: number | null;
        new_customers_only: boolean;
    }

    async function validatePromoCode(code: string): Promise<boolean> {
        try {
            const params = new URLSearchParams({
                code,
                license: selectedPlanType.value,
                term: selectedTerm.value.toString(),
                quantity: licenseCount.value.toString()
            });

            const { data } = await usePbApi<{ data: PromoCodeValidationData }>(`/promo-codes/validate?${params.toString()}`, {
                method: 'GET',
            });

            if (data?.data?.valid === true) {
                promoCodeData.value = data.data;
                return true;
            }
            return false;
        } catch (error: unknown) {
            console.error('Error validating promo code:', error);
            throw error;
        }
    }

    // Clear promo code
    function clearPromoCode() {
        promoCode.value = null;
        promoCodeData.value = null;
        promoCodeApplied.value = false;

        // Remove promo cookie if it was used
        const promoCookie = useCookie('pb-promo');
        if (promoCookie.value) {
            promoCookie.value = null;
        }
    }

    // Check for promo code in cookies and apply if valid
    async function checkAndApplyPromoFromCookie(): Promise<boolean> {
        try {
            const pbPromoCookie = useCookie('pb-promo');
            const cookiePromoCode = pbPromoCookie.value;
            
            if (!cookiePromoCode || typeof cookiePromoCode !== 'string') {
                return false;
            }

            // Store the URL promo code for reference
            urlPromoCode.value = cookiePromoCode;
            promoCode.value = cookiePromoCode;

            // Try to validate the promo code
            const isValid = await validatePromoCode(cookiePromoCode);
            
            if (isValid) {
                promoCodeApplied.value = true;
                return true;
            } else {
                // Clear if invalid
                promoCode.value = null;
                urlPromoCode.value = null;
                return false;
            }
        } catch (error) {
            console.error('Error applying promo code from cookie:', error);
            promoCode.value = null;
            urlPromoCode.value = null;
            return false;
        }
    }

    // Computed getters for purchase cost breakdown
    const totalCostBeforeDiscounts = computed(() => {
        // For upgrades, wait for API data - don't show fallback values while loading
        if (purchaseType.value === PurchaseType.UPGRADE) {
            if (isLoadingUpgradePricing.value || !upgradePricing.value) {
                return 0;
            }
            return upgradePricing.value.calculation.base_price;
        }
        return getTotalCostBeforeDiscounts(selectedPlanType.value);
    });

    const totalAnnualDiscount = computed(() => {
        // For upgrades, wait for API data - don't show fallback values while loading
        if (purchaseType.value === PurchaseType.UPGRADE) {
            if (isLoadingUpgradePricing.value || !upgradePricing.value) {
                return 0;
            }
            const annualDiscount = upgradePricing.value.pricing.discounts.find(d => d.name === 'Annual Discount');
            return annualDiscount ? Math.abs(annualDiscount.amount) : 0;
        }
        return getTotalAnnualDiscount(selectedPlanType.value);
    });

    const totalMultiLicenseDiscount = computed(() => {
        // For upgrades, wait for API data - don't show fallback values while loading
        if (purchaseType.value === PurchaseType.UPGRADE) {
            if (isLoadingUpgradePricing.value || !upgradePricing.value) {
                return 0;
            }
            const multiDiscount = upgradePricing.value.pricing.discounts.find(d => d.name === 'Multi-License Discount');
            return multiDiscount ? Math.abs(multiDiscount.amount) : 0;
        }
        return getTotalMultiLicenseDiscount(selectedPlanType.value);
    });

    const totalCostAfterDiscounts = computed(() => {
        // For upgrades, wait for API data - don't show fallback values while loading
        if (purchaseType.value === PurchaseType.UPGRADE) {
            if (isLoadingUpgradePricing.value || !upgradePricing.value) {
                return 0;
            }
            
            let finalCost = upgradePricing.value.calculation.final_amount_due;
            
            // Apply gift card if available
            if (giftCertAppliedAmount.value > 0) {
                const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, finalCost);
                finalCost = Math.max(0, finalCost - giftCardAmountUsed);
            }

            // Apply credit balance if available
            if (billingStore.creditBalance > 0) {
                const creditAmountUsed = Math.min(billingStore.creditBalance * 100, finalCost);
                finalCost = Math.max(0, finalCost - creditAmountUsed);
            }

            return finalCost;
        }
        return getTotalCostAfterDiscounts(selectedPlanType.value);
    });

    // Credit balance from billing store
    const creditBalance = computed(() => billingStore.creditBalance);
    const isCreditBalanceLoading = computed(() => billingStore.isCreditBalanceLoading);

    // Credit amount used in this transaction
    const creditAmountUsed = computed(() => getCreditAmountUsed(selectedPlanType.value));

    // Helper functions that accept LicenseType parameter
    function getTotalCostBeforeDiscounts(licenseType: LicenseType = selectedPlanType.value) {
        // For upgrades, use API base price
        if (purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value) {
            return upgradePricing.value.calculation.base_price;
        }

        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        // Total cost before any discounts = count × base_monthly × term
        const basePriceForTerm = licenseData.base_price_monthly * selectedTerm.value;
        return basePriceForTerm * licenseCount.value;
    }

    function getTotalAnnualDiscount(licenseType: LicenseType = selectedPlanType.value) {
        // For upgrades, find annual discount from API discounts array
        if (purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value) {
            const annualDiscount = upgradePricing.value.pricing.discounts.find(d => d.name === 'Annual Discount');
            return annualDiscount ? Math.abs(annualDiscount.amount) : 0;
        }

        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        // Get the cost per license after annual discount (no multi-license discount)
        const costPerLicenseAfterAnnualDiscount = getCostPerLicenseAfterAnnualDiscount(licenseType);
        
        // Calculate total cost before any discounts
        const basePriceForTerm = licenseData.base_price_monthly * selectedTerm.value;
        const totalCostBeforeDiscounts = basePriceForTerm * licenseCount.value;
        
        // Calculate total cost after annual discount only
        const totalCostAfterAnnualDiscount = costPerLicenseAfterAnnualDiscount * licenseCount.value;
        
        // Annual discount = total cost before discounts - total cost after annual discount
        return totalCostBeforeDiscounts - totalCostAfterAnnualDiscount;
    }

    function getTotalMultiLicenseDiscount(licenseType: LicenseType = selectedPlanType.value) {
        // For upgrades, find multi-license discount from API discounts array
        if (purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value) {
            const multiDiscount = upgradePricing.value.pricing.discounts.find(d => d.name === 'Multi-License Discount');
            return multiDiscount ? Math.abs(multiDiscount.amount) : 0;
        }

        const licenseData = licenseCosts.value?.[licenseType];
        if (!licenseData) return 0;
        
        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];
        
        // Calculate what the cost would be without multi-license discounts (just annual discount)
        const costPerLicenseAfterAnnualDiscount = getCostPerLicenseAfterAnnualDiscount(licenseType);
        const costWithOnlyAnnualDiscount = costPerLicenseAfterAnnualDiscount * licenseCount.value;
        
        // Calculate the actual cost by summing up individual license prices
        let actualCost = 0;
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                actualCost += pricingTier.price_per_license;
            }
        }
        
        // Multi-license discount = cost with only annual discount - actual cost
        return costWithOnlyAnnualDiscount - actualCost;
    }

    function getTotalCostAfterDiscounts(licenseType: LicenseType = selectedPlanType.value) {
        // For upgrades, use API pricing directly
        if (purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value) {
            // Start with the API's calculated amount
            let finalCost = upgradePricing.value.calculation.final_amount_due;

            // Apply gift card if available
            if (giftCertAppliedAmount.value > 0) {
                const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, finalCost);
                finalCost = Math.max(0, finalCost - giftCardAmountUsed);
            }

            // Apply credit balance if available
            if (billingStore.creditBalance > 0) {
                const creditAmountUsed = Math.min(billingStore.creditBalance * 100, finalCost);
                finalCost = Math.max(0, finalCost - creditAmountUsed);
            }

            return finalCost;
        }

        // For regular purchases, calculate from license costs API
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];

        // Calculate final cost from API pricing
        let finalCost = 0;
        let firstLicensePrice = 0;
        
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                const licensePrice = pricingTier.price_per_license;
                if (i === 1) {
                    firstLicensePrice = licensePrice; // Store first license final price
                }
                finalCost += licensePrice;
            }
        }
        
        // Apply promo code discount if available (to first license only, on FINAL price)
        if (promoCodeApplied.value && promoCodeData.value && firstLicensePrice > 0) {
            // Calculate discount on the FIRST license's final price (after term + multi-license discounts)
            let promoDiscount = 0;
            if (promoCodeData.value.discount_type === 'percentage') {
                promoDiscount = Math.round(firstLicensePrice * (promoCodeData.value.discount_value / 100));
            } else {
                // Fixed amount
                promoDiscount = promoCodeData.value.discount_value;
            }
            finalCost = Math.max(0, finalCost - promoDiscount);
        }
        
        // Apply gift card if available
        if (giftCertAppliedAmount.value > 0) {
            const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, finalCost);
            finalCost = Math.max(0, finalCost - giftCardAmountUsed);
        }

        // Apply credit balance if available
        if (billingStore.creditBalance > 0) {
            const creditAmountUsed = Math.min(billingStore.creditBalance * 100, finalCost);
            finalCost = Math.max(0, finalCost - creditAmountUsed);
        }

        return finalCost;
    }

    // Calculate the amount of credit balance used in this transaction
    function getCreditAmountUsed(licenseType: LicenseType = selectedPlanType.value) {
        if (!licenseCosts.value || billingStore.creditBalance <= 0) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];

        // Calculate cost before credit balance
        let costBeforeCredit = 0;
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                costBeforeCredit += pricingTier.price_per_license;
            }
        }
        
        // Apply gift card if available
        if (giftCertAppliedAmount.value > 0) {
            const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, costBeforeCredit);
            costBeforeCredit = Math.max(0, costBeforeCredit - giftCardAmountUsed);
        }

        // Calculate how much credit balance to use
        const creditAmountUsed = Math.min(billingStore.creditBalance * 100, costBeforeCredit);
        
        return creditAmountUsed;
    }

    // Function to get cost per license after annual discount
    function getCostPerLicenseAfterAnnualDiscount(licenseType: LicenseType = selectedPlanType.value): number {
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        // Get the pricing for the first license (no multi-license discount)
        const firstLicensePricing = termPricing.multi_licenses.find(m => m.count === 1);
        if (!firstLicensePricing) return 0;

        // This is the cost per license after annual discount
        return firstLicensePricing.price_per_license;
    }

    // Calculate prorated discount for upgrade
    // Now uses API data for perfect accuracy - single source of truth
    function getProratedUpgradeDiscount(licenseType: LicenseType = selectedPlanType.value): number {
        if (purchaseType.value !== PurchaseType.UPGRADE || !upgradePricing.value) {
            return 0;
        }

        // Return the old license credit (trade-in value) from the API calculation
        return upgradePricing.value.calculation.old_license_credit;
    }

    // Get days remaining until next renewal (for display)
    // Uses API data when available for upgrades, otherwise calculates locally
    const daysUntilRenewal = computed(() => {
        // Use API data if we're upgrading and have pricing info
        if (purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value) {
            return upgradePricing.value.calculation.days_remaining;
        }

        // Fallback to local calculation
        if (!license.value?.next_bill_date) {
            return 0;
        }

        const now = new Date();
        const nextBillDate = new Date(license.value.next_bill_date);
        
        // Add 1 to match backend calculation (same-day upgrades = 365 days)
        const days = Math.floor((nextBillDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        return Math.max(0, days);
    });

    // Get the prorated discount amount (for display)
    const proratedUpgradeDiscount = computed(() => {
        if (purchaseType.value !== PurchaseType.UPGRADE || !upgradePricing.value) {
            return 0;
        }
        // Directly return from upgradePricing for reactivity
        return upgradePricing.value.calculation.old_license_credit;
    });

    async function assignLicense(licenseId: number, contact: PurchaseLicenseFor, showToast: boolean = true) {
        const authStore = useAuthStore();
        const { showSuccess, showError } = useGlobalToast();

        if (!authStore.user?.identity?.id) {
            showError("Error", 'User not found');
            return;
        }

        try {
            await licenseService.assignLicense(authStore.user.identity.id, licenseId, contact);
            // Refresh person data to update license availability
            await personStore.getPerson();
            if (showToast) {
                showSuccess("Success", `License assigned to ${contact.name} successfully!`);
            }
        } catch (error: any) {
            showError("Error", error.message || 'Failed to assign license');
            throw error; // Re-throw to let callers handle it
        }
    }

    // Fetch credit balance when subscription modal is initialized
    async function fetchCreditBalance() {
        try {
            await billingStore.fetchCreditBalance();
        } catch (error) {
            console.error('Failed to fetch credit balance:', error);
        }
    }

    // Watch for changes in upgrade parameters and automatically fetch new pricing
    watch(
        [selectedPlanType, selectedTerm, license, purchaseType],
        () => {
            // Only fetch if we're in upgrade mode
            if (purchaseType.value === PurchaseType.UPGRADE && license.value) {
                fetchUpgradePricing();
            }
        },
        { immediate: true }
    );

    // Computed property for promo discount amount (for display in summary)
    const promoDiscountAmount = computed(() => {
        if (!promoCodeApplied.value || !promoCodeData.value) {
            return 0;
        }

        const licenseData = licenseCosts.value?.[selectedPlanType.value];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];

        // Get first license final price
        const firstLicensePosition = existingCount + 1;
        let pricingTier;
        if (firstLicensePosition === 1) {
            pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
        } else if (firstLicensePosition === 2) {
            pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
        } else if (firstLicensePosition === 3) {
            pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
        } else {
            pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
        }

        if (pricingTier) {
            const firstLicenseFinalPrice = pricingTier.price_per_license;
            
            if (promoCodeData.value.discount_type === 'percentage') {
                return Math.round(firstLicenseFinalPrice * (promoCodeData.value.discount_value / 100));
            } else {
                return Math.min(promoCodeData.value.discount_value, firstLicenseFinalPrice);
            }
        }

        return 0;
    });

    return {
        selectedTerm,
        selectedPlanType,
        licenseCount,
        benefits: LICENSE_BENEFITS,
        totalCostBeforeDiscounts,
        totalAnnualDiscount,
        totalMultiLicenseDiscount,
        totalCostAfterDiscounts,
        creditBalance,
        isCreditBalanceLoading,
        creditAmountUsed,
        getTotalCostBeforeDiscounts,
        getTotalAnnualDiscount,
        getTotalMultiLicenseDiscount,
        getTotalCostAfterDiscounts,
        getCostPerLicenseAfterAnnualDiscount,
        paymentMethodId,
        selectedUserType,
        isUpdateCurrentLicense,
        isSimulationUpgrade,
        setGiftCertificateDetails,
        applyGiftCertificate,
        clearGiftCard,
        giftCertCode,
        giftCertAmount,
        giftCertAppliedCode,
        giftCertAppliedAmount,
        giftCertEmail,
        giftCertName,
        purchasingFor,
        purchaseType,
        license,
        licenseCosts,
        isLoadingLicenseCosts,
        fetchLicenseCosts,
        upgradePricing,
        isLoadingUpgradePricing,
        fetchUpgradePricing,
        assignLicense,
        fetchCreditBalance,
        getCreditAmountUsed,
        daysUntilRenewal,
        proratedUpgradeDiscount,
        // Promo code
        promoCode,
        promoCodeData,
        promoCodeApplied,
        urlPromoCode,
        promoDiscountAmount,
        validatePromoCode,
        clearPromoCode,
        checkAndApplyPromoFromCookie
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSubscribeStore, import.meta.hot))
}